import * as React from 'react';
import {
    Button,
    Form,
    PageSection,
    Spinner,
    Tooltip
} from '@patternfly/react-core';
import {useState, useEffect, useRef} from "react";
import {createAd, getPresignedUrl, uploadToS3, getOrgUploadUrl, getOrganizations, updateAd, getSubscription} from "@app/utils/utils";
import {ActivationError, SubscriptionResponse} from "@app/Subscription/types/subscription";
import {useNavigate} from "react-router-dom";
import {
    TimesIcon,
    BuildingIcon,
    CheckCircleIcon,
    UploadIcon,
    ArrowRightIcon,
    ArrowLeftIcon,
    AutomationIcon,
    LockIcon,
    EditIcon,
    CheckIcon
} from "@patternfly/react-icons";
import { AppModal } from '@app/components/AppModal';
import { TopUpModal } from '@app/Subscription/TopUpModal';
import { LogoBadge } from '@app/components/LogoBadge';
import { GradientButton } from '@app/components/GradientButton';
import { BorderedInput } from '@app/Login/BorderedInput';
import { LedCarPreview } from '@app/Ads/LedCarPreview';
import { PURPLE_GRADIENT, colors, radii, fieldLabelStyle, formResetStyle } from '@app/components/designTokens';


export interface ICreateAdProps {
    triggerRefresh: Function;
    isOpen?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
    hideButton?: boolean;
    existingAds?: any[];
    initialAdType?: 'upload' | 'ai';
    // 'modal' (default) renders inside AppModal; 'page' renders full-width
    // inside the content window (used by the /ads/new route). In page mode the
    // wizard is always "open" and cancel/completion navigate back to /ads.
    displayMode?: 'modal' | 'page';
}

const CreateAdWizardModal: React.FunctionComponent<ICreateAdProps> = (props) => {

    const [internalModalOpen, setInternalModalOpen] = React.useState(false);

    const isPageMode = props.displayMode === 'page';
    // In page mode the wizard is always mounted/open; otherwise fall back to the
    // controlled `isOpen` prop or internal state.
    const isModalOpen = isPageMode
        ? true
        : (props.isOpen !== undefined ? props.isOpen : internalModalOpen);
    const setModalOpen = (open: boolean) => {
        if (props.onOpenChange) {
            props.onOpenChange(open);
        } else {
            setInternalModalOpen(open);
        }
    };
    const [name, setName] = useState('');
    const [ad_id, setAdId] = useState('');
    const [org_id, setOrgId] = useState<string>('');
    const [file, setFile] = useState<File | null>(null);
    const [currentStep, setCurrentStep] = useState(1);
    const [organizations, setOrganizations] = useState<any[]>([]);
    const [isRegistering, setIsRegistering] = useState(false);
    const [isRegistered, setIsRegistered] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isComplete, setIsComplete] = useState(false);
    const [adType, setAdType] = useState<'upload' | 'ai' | null>(null);
    const [aiPrompt, setAiPrompt] = useState('');
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    // LED-fit validation for the uploaded ad media (see LED_* constants below).
    const [fileValid, setFileValid] = useState(false);
    const [fileChecking, setFileChecking] = useState(false);
    const [validationMsg, setValidationMsg] = useState<string | null>(null);
    const [fileDims, setFileDims] = useState<{ width: number; height: number } | null>(null);
    // Optional reference images for AI ads (logo / headshot / product shots).
    // Uploaded to an org-scoped S3 path before createAd; URIs passed via payload.
    const [referenceImages, setReferenceImages] = useState<Array<{
        id: string;
        file: File;
        previewUrl: string;
        s3Uri?: string;
        uploading: boolean;
        error?: boolean;
    }>>([]);
    const [subscription, setSubscription] = useState<SubscriptionResponse | null>(null);
    const [upgradeModal, setUpgradeModal] = useState<{ message: string; ctaTier?: string; offerTopUp?: boolean } | null>(null);
    // One-off "buy AI ad credits" modal — offered alongside upgrade when the quota is used up.
    const [showTopUp, setShowTopUp] = useState(false);
    // Friendly errors surfaced to the user instead of a raw 500. nameError is
    // shown on the name step (duplicate name); createError is a generic failure
    // shown on the register step.
    const [nameError, setNameError] = useState<string | null>(null);
    const [createError, setCreateError] = useState<string | null>(null);
    // Page-mode split layout collapses to a stacked layout on narrow widths.
    const [isNarrow, setIsNarrow] = useState(false);

    const navigate = useNavigate();
    const nameInputRef = useRef<HTMLInputElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const refImageInputRef = useRef<HTMLInputElement>(null);

    const aiLimit = subscription ? subscription.aiAds.limit : null;
    const aiUsed = subscription ? subscription.aiAds.used : 0;
    // Purchased top-up credits keep AI unlocked — whether the tier excludes AI or the quota is used up.
    const aiPurchased = subscription ? (subscription.aiAds.purchased ?? 0) : 0;
    const aiNotInTier = subscription && aiLimit === 0 && aiPurchased === 0;
    const aiQuotaExhausted =
        subscription && aiLimit !== null && aiLimit > 0 && aiUsed >= aiLimit && aiPurchased === 0;
    const aiUnlimited = subscription && aiLimit === null;
    const aiBlocked = !!aiNotInTier || !!aiQuotaExhausted;
    const aiBlockedReason: string | null = aiNotInTier
        ? 'Buy AI ad credits, or upgrade to Silver to unlock AI ad generation'
        : aiQuotaExhausted
            ? `${aiUsed}/${aiLimit} AI ads used this period. Buy extra credits, or upgrade to Gold for unlimited.`
            : null;

    const handleUploadClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const getMediaType = (file: File): 'IMAGE' | 'VIDEO' => {
        return file.type.startsWith('video/') ? 'VIDEO' : 'IMAGE';
    };

    // Ads render on a 384 x 128 px LED panel (a 3:1 wide strip). Media must
    // match that aspect ratio and be at least native resolution so it isn't
    // stretched, cropped, or upscaled/blurry on the physical screen.
    const LED_W = 384;
    const LED_H = 128;
    const LED_RATIO = LED_W / LED_H; // 3.0
    const RATIO_TOL = 0.05; // accept ~2.85:1 – 3.15:1

    // Reads a media file's intrinsic dimensions (image or video) so we can
    // validate it against the LED panel. Resolves to null if it can't be read.
    const readMediaDimensions = (mediaFile: File): Promise<{ width: number; height: number } | null> => {
        return new Promise((resolve) => {
            const objectUrl = URL.createObjectURL(mediaFile);
            const cleanup = () => URL.revokeObjectURL(objectUrl);
            if (mediaFile.type.startsWith('video/')) {
                const video = document.createElement('video');
                video.preload = 'metadata';
                video.onloadedmetadata = () => {
                    const dims = { width: video.videoWidth, height: video.videoHeight };
                    cleanup();
                    resolve(dims.width && dims.height ? dims : null);
                };
                video.onerror = () => { cleanup(); resolve(null); };
                video.src = objectUrl;
            } else {
                const img = new Image();
                img.onload = () => {
                    const dims = { width: img.naturalWidth, height: img.naturalHeight };
                    cleanup();
                    resolve(dims.width && dims.height ? dims : null);
                };
                img.onerror = () => { cleanup(); resolve(null); };
                img.src = objectUrl;
            }
        });
    };

    // Formats a ratio like 1.0 -> "1:1", 3.0 -> "3:1" for user-facing messages.
    const formatRatio = (w: number, h: number): string => {
        const r = w / h;
        return `${r.toFixed(r < 10 ? 2 : 0).replace(/\.?0+$/, '')}:1`;
    };

    // Hard-block validation: returns an error message (string) if the media is
    // not LED-ready, or null if it passes.
    const validateLedMedia = (dims: { width: number; height: number } | null): string | null => {
        if (!dims) {
            return 'Could not read this file. Please choose a valid image or video.';
        }
        const { width, height } = dims;
        const ratio = width / height;
        if (Math.abs(ratio - LED_RATIO) > RATIO_TOL) {
            return `This file is ${width}×${height} (${formatRatio(width, height)}). Ads must use a 3:1 ratio — e.g. ${LED_W}×${LED_H}, 768×256, or 1152×384.`;
        }
        if (width < LED_W || height < LED_H) {
            return `This file is only ${width}×${height}. It must be at least ${LED_W}×${LED_H} so it stays sharp on the LED screen.`;
        }
        return null;
    };

    const extractS3Uri = (presignedUrl: string): string => {
        try {
            const url = new URL(presignedUrl);
            const hostname = url.hostname;
            const bucketMatch = hostname.match(/^([^.]+)\.s3\./);
            if (bucketMatch) {
                const bucket = bucketMatch[1];
                const key = decodeURIComponent(url.pathname.substring(1));
                return `s3://${bucket}/${key}`;
            }
            return presignedUrl;
        } catch {
            return presignedUrl;
        }
    };

    const effectiveAdType = props.initialAdType || adType;
    const skipTypeStep = props.initialAdType !== undefined;
    // AI path gains an optional reference-images step (after the prompt).
    const totalSteps = effectiveAdType === 'ai'
        ? (skipTypeStep ? 5 : 6)
        : (skipTypeStep ? 5 : 6);

    const generateUniqueName = (existingAds: any[]) => {
        const existingNames = existingAds.map(function(ad) {
            return ad.name ? ad.name.toLowerCase() : '';
        });
        let counter = 1;
        let candidateName = 'My Ad ' + counter;
        while (existingNames.indexOf(candidateName.toLowerCase()) !== -1) {
            counter++;
            candidateName = 'My Ad ' + counter;
        }
        return candidateName;
    };

    useEffect(() => {
        if (!isPageMode) {
            return;
        }
        const mq = window.matchMedia('(max-width: 900px)');
        const update = () => setIsNarrow(mq.matches);
        update();
        mq.addEventListener('change', update);
        return () => mq.removeEventListener('change', update);
    }, [isPageMode]);

    // The backend enforces a unique ad name per organization. Check the loaded
    // ad list up front so we can show a friendly message instead of a raw 500.
    const isDuplicateAdName = (candidate: string, orgId: string): boolean => {
        const target = candidate.trim().toLowerCase();
        if (!target || !orgId) {
            return false;
        }
        return (props.existingAds || []).some((ad: any) =>
            ad && typeof ad.name === 'string' &&
            ad.name.trim().toLowerCase() === target &&
            String(ad.organizationId ?? '') === String(orgId)
        );
    };

    useEffect(() => {
        if (isModalOpen) {
            if (props.initialAdType) {
                setAdType(props.initialAdType);
            }
            const uniqueName = generateUniqueName(props.existingAds || []);
            setName(uniqueName);
            const nameStepNumber = skipTypeStep ? 1 : 2;
            if (currentStep === nameStepNumber) {
                setTimeout(() => {
                    if (nameInputRef.current) {
                        nameInputRef.current.select();
                    }
                }, 100);
            }
            getOrganizations().then(profile => {
                if (profile && profile.organizations) {
                    setOrganizations(profile.organizations);
                    if (profile.organizations.length === 1) {
                        setOrgId(profile.organizations[0].id);
                    }
                }
            });
            getSubscription()
                .then((sub) => setSubscription(sub))
                .catch(() => setSubscription(null));
        }
    }, [isModalOpen]);

    const resetForm = () => {
        setName('');
        setOrgId('');
        setFile(null);
        setAdId('');
        setCurrentStep(1);
        setIsRegistering(false);
        setIsRegistered(false);
        setIsUploading(false);
        setIsComplete(false);
        setAdType(null);
        setAiPrompt('');
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
        }
        setPreviewUrl(null);
        setFileValid(false);
        setFileChecking(false);
        setValidationMsg(null);
        setFileDims(null);
        setNameError(null);
        setCreateError(null);
        referenceImages.forEach(r => URL.revokeObjectURL(r.previewUrl));
        setReferenceImages([]);
    };

    const handleModalToggle = () => {
        if (isModalOpen) {
            resetForm();
            props.triggerRefresh(true);
        }
        setModalOpen(!isModalOpen);
    };

    // Unified exit used by cancel + completion. In page mode the wizard is a
    // route, so we navigate back to /ads (which refetches the list on mount);
    // in modal mode we close the modal.
    const dismiss = () => {
        if (isPageMode) {
            navigate('/ads');
        } else {
            handleModalToggle();
        }
    };

    const handleNext = async () => {
        const registerStep = effectiveAdType === 'ai'
            ? (skipTypeStep ? 5 : 6)
            : (skipTypeStep ? 3 : 4);
        const finalStep = totalSteps;

        if (currentStep === registerStep && !isRegistered) {
            const nameStep = skipTypeStep ? 1 : 2;
            // Catch duplicate names before hitting the API (per-org unique name).
            if (isDuplicateAdName(name, org_id)) {
                setNameError(`You already have an ad named "${name.trim()}". Please choose a different name.`);
                setCreateError(null);
                setCurrentStep(nameStep);
                return;
            }
            setNameError(null);
            setCreateError(null);
            setIsRegistering(true);
            try {
                const apiAdType = effectiveAdType === 'ai' ? 'GENERATE_WITH_AI' : 'UPLOAD_MEDIA';
                const payload: any = {
                    "name": name,
                    "tags": ["default"],
                    "organizationId": org_id,
                    "adType": apiAdType
                };
                if (effectiveAdType === 'ai' && aiPrompt.trim()) {
                    payload.prompt = aiPrompt.trim();
                }
                if (effectiveAdType === 'ai') {
                    const refUris = referenceImages.filter(r => r.s3Uri).map(r => r.s3Uri);
                    if (refUris.length > 0) {
                        payload.referenceImageS3Uris = refUris;
                    }
                }
                const result = await createAd(payload);
                setAdId(result.id);
                setIsRegistered(true);
                props.triggerRefresh(new Date());
                if (effectiveAdType === 'ai') {
                    setIsComplete(true);
                    setTimeout(function() {
                        dismiss();
                    }, 1500);
                }
            } catch (error: any) {
                console.error('Failed to register ad:', error);
                if (error instanceof ActivationError) {
                    if (error.body.reason === 'ai_not_in_tier') {
                        setUpgradeModal({
                            message: 'Upgrade to Silver to unlock AI ad generation, or buy AI ad credits individually.',
                            ctaTier: error.body.upgradeTo || 'SILVER',
                            offerTopUp: true
                        });
                    } else if (error.body.reason === 'ai_limit') {
                        setUpgradeModal({
                            message: 'You\'ve hit your AI ad limit for this period. Buy extra credits, or upgrade to Gold for unlimited.',
                            ctaTier: error.body.upgradeTo || 'GOLD',
                            offerTopUp: true
                        });
                    } else if (error.body.reason === 'no_active_subscription') {
                        setUpgradeModal({
                            message: 'Subscribe to a plan, or buy AI ad credits individually to create AI ads.',
                            offerTopUp: true
                        });
                    }
                } else if (error && (error.status === 409 || isDuplicateAdName(name, org_id))) {
                    // Duplicate name that slipped past the pre-check (e.g. a stale
                    // list, or a name created elsewhere since load).
                    setNameError(`You already have an ad named "${name.trim()}". Please choose a different name.`);
                    setCurrentStep(nameStep);
                } else {
                    setCreateError('Something went wrong creating your ad. Please try again.');
                }
            } finally {
                setIsRegistering(false);
            }
            return;
        }

        if (currentStep === finalStep && effectiveAdType !== 'ai') {
            if (file && ad_id) {
                setIsUploading(true);
                try {
                    const data = await getPresignedUrl(org_id, ad_id, file.name.trim());
                    await uploadToS3(data['url'], file);
                    const mediaPayload = {
                        name: name,
                        media: {
                            uri: extractS3Uri(data['url']),
                            type: getMediaType(file),
                            provider: 'S3'
                        }
                    };
                    await updateAd(ad_id, mediaPayload);
                    setIsComplete(true);
                    props.triggerRefresh(new Date());
                    setTimeout(function() {
                        dismiss();
                    }, 1500);
                } catch (error) {
                    console.error('Failed to upload:', error);
                } finally {
                    setIsUploading(false);
                }
            }
            return;
        }

        if (currentStep < totalSteps) {
            setCurrentStep(currentStep + 1);
            const nameStep = skipTypeStep ? 1 : 2;
            if (currentStep + 1 === nameStep) {
                setTimeout(() => {
                    if (nameInputRef.current) {
                        nameInputRef.current.select();
                    }
                }, 100);
            }
        }
    };

    const handleBack = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    const canProceed = () => {
        if (skipTypeStep) {
            if (effectiveAdType === 'ai') {
                switch (currentStep) {
                    case 1: return name.trim() !== '';
                    case 2: return org_id !== '';
                    case 3: return aiPrompt.trim().length >= 10;
                    case 4: return !referenceImages.some(r => r.uploading); // optional; wait for uploads
                    case 5: return true;
                    default: return false;
                }
            } else {
                switch (currentStep) {
                    case 1: return name.trim() !== '';
                    case 2: return org_id !== '';
                    case 3: return true;
                    case 4: return file !== null && fileValid;
                    case 5: return true;
                    default: return false;
                }
            }
        } else {
            if (effectiveAdType === 'ai') {
                switch (currentStep) {
                    case 1: return adType !== null;
                    case 2: return name.trim() !== '';
                    case 3: return org_id !== '';
                    case 4: return aiPrompt.trim().length >= 10;
                    case 5: return !referenceImages.some(r => r.uploading); // optional; wait for uploads
                    case 6: return true;
                    default: return false;
                }
            } else {
                switch (currentStep) {
                    case 1: return adType !== null;
                    case 2: return name.trim() !== '';
                    case 3: return org_id !== '';
                    case 4: return true;
                    case 5: return file !== null && fileValid;
                    case 6: return true;
                    default: return false;
                }
            }
        }
    };

    // Ordered step labels for the current path. Drives both the step title and
    // the vertical stepper in page mode. Before a type is chosen (non-skip
    // step 1), we fall back to the upload path's labels.
    const getStepList = (): string[] => {
        if (effectiveAdType === 'ai') {
            return skipTypeStep
                ? ['Name your ad', 'Choose organization', 'Describe your ad', 'Add reference images', 'Create ad']
                : ['Choose ad type', 'Name your ad', 'Choose organization', 'Describe your ad', 'Add reference images', 'Create ad'];
        }
        return skipTypeStep
            ? ['Name your ad', 'Choose organization', 'Register ad', 'Upload media', 'Review & finish']
            : ['Choose ad type', 'Name your ad', 'Choose organization', 'Register ad', 'Upload media', 'Review & finish'];
    };

    const getStepTitle = () => getStepList()[currentStep - 1] || '';

    const styles = {
        // --- Full-page layout (page mode) ---
        // Single column that fills the content window. The stepper runs
        // horizontally across the top so it never competes with the app's own
        // left nav, and there's no card so it reads as a page, not a dialog.
        pageWrap: {
            display: 'flex',
            flexDirection: 'column' as const,
            minHeight: '100%',
            background: '#ffffff'
        } as React.CSSProperties,
        pageHeader: {
            padding: isNarrow ? '26px 20px 0' : '44px 44px 0',
            borderBottom: `1px solid ${colors.border}`,
            background: 'linear-gradient(180deg, #faf5ff 0%, #ffffff 100%)'
        } as React.CSSProperties,
        centered: {
            maxWidth: '860px',
            margin: '0 auto',
            width: '100%'
        } as React.CSSProperties,
        headerTopRow: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: '16px',
            flexWrap: 'wrap' as const
        } as React.CSSProperties,
        ledPill: {
            display: isNarrow ? 'none' : 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '7px 12px',
            borderRadius: radii.pill,
            border: `1px solid ${colors.border}`,
            background: '#ffffff',
            fontSize: '12px',
            fontWeight: 600,
            color: colors.subtle,
            whiteSpace: 'nowrap' as const,
            marginTop: '4px'
        } as React.CSSProperties,
        ledSwatch: {
            width: '30px',
            height: '10px',
            borderRadius: '2px',
            background: '#0f172a',
            backgroundImage: 'radial-gradient(rgba(129, 140, 248, 0.7) 0.9px, transparent 1.2px)',
            backgroundSize: '5px 5px',
            flexShrink: 0
        } as React.CSSProperties,
        stepperH: {
            listStyle: 'none',
            display: 'flex',
            padding: 0,
            margin: isNarrow ? '20px 0 20px' : '30px 0 26px'
        } as React.CSSProperties,
        pageBody: {
            flex: 1,
            padding: isNarrow ? '26px 20px 8px' : '38px 44px 16px'
        } as React.CSSProperties,
        pageFooter: {
            borderTop: `1px solid ${colors.border}`,
            padding: isNarrow ? '16px 20px 24px' : '20px 44px 30px'
        } as React.CSSProperties,
        eyebrow: {
            margin: 0,
            fontSize: '12px',
            fontWeight: 700,
            letterSpacing: '2px',
            textTransform: 'uppercase' as const,
            color: colors.boltGold
        } as React.CSSProperties,
        pageTitle: {
            margin: '10px 0 0',
            fontSize: isNarrow ? '26px' : '32px',
            fontWeight: 800,
            color: colors.title,
            letterSpacing: '-1px',
            lineHeight: 1.1
        } as React.CSSProperties,
        body: {
            padding: '32px 32px 16px',
            minHeight: '320px'
        } as React.CSSProperties,
        header: {
            textAlign: 'center' as const,
            marginBottom: '24px'
        },
        title: {
            color: colors.title,
            fontSize: '24px',
            fontWeight: 700,
            margin: 0,
            marginBottom: '6px',
            letterSpacing: '-0.5px'
        },
        subtitle: {
            color: colors.subtle,
            fontSize: '14px',
            margin: 0
        },
        stepIndicator: {
            display: 'flex',
            justifyContent: 'center',
            gap: '8px',
            marginTop: '18px'
        },
        stepDot: {
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: colors.border,
            transition: 'all 0.3s'
        },
        stepDotActive: {
            width: '24px',
            height: '8px',
            borderRadius: '4px',
            background: PURPLE_GRADIENT,
            transition: 'all 0.3s'
        },
        stepDotCompleted: {
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: colors.accent,
            transition: 'all 0.3s'
        },
        footer: {
            padding: '16px 32px 24px',
            display: 'flex',
            justifyContent: 'space-between',
            gap: '12px',
            borderTop: `1px solid ${colors.border}`
        },
        orgCard: {
            border: `1px solid ${colors.border}`,
            borderRadius: '12px',
            padding: '14px',
            marginBottom: '10px',
            cursor: 'pointer',
            transition: 'all 0.15s',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            background: '#ffffff'
        } as React.CSSProperties,
        orgCardSelected: {
            border: `1px solid ${colors.accent}`,
            borderRadius: '12px',
            padding: '14px',
            marginBottom: '10px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            background: '#f5f3ff',
            boxShadow: `0 0 0 3px ${colors.accentRing}`
        } as React.CSSProperties,
        orgIcon: {
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            background: PURPLE_GRADIENT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#ffffff',
            flexShrink: 0
        },
        orgName: {
            fontSize: '15px',
            fontWeight: 600,
            color: colors.title
        },
        orgCity: {
            fontSize: '13px',
            color: colors.subtle
        },
        uploadArea: {
            border: `2px dashed ${colors.border}`,
            borderRadius: '14px',
            padding: '36px 24px',
            textAlign: 'center' as const,
            cursor: 'pointer',
            transition: 'all 0.15s',
            background: '#fafafa'
        } as React.CSSProperties,
        uploadAreaActive: {
            border: `2px dashed ${colors.accent}`,
            borderRadius: '14px',
            padding: '24px',
            textAlign: 'center' as const,
            cursor: 'pointer',
            background: '#f5f3ff'
        } as React.CSSProperties,
        uploadIcon: {
            width: '52px',
            height: '52px',
            borderRadius: '14px',
            background: PURPLE_GRADIENT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#ffffff',
            margin: '0 auto 14px',
            fontSize: '22px'
        },
        reviewItem: {
            display: 'flex',
            justifyContent: 'space-between',
            padding: '12px 0',
            borderBottom: `1px solid ${colors.border}`
        },
        reviewLabel: {
            color: colors.subtle,
            fontSize: '14px'
        },
        reviewValue: {
            color: colors.title,
            fontSize: '14px',
            fontWeight: 600
        },
        spinnerContainer: {
            display: 'flex',
            flexDirection: 'column' as const,
            alignItems: 'center',
            justifyContent: 'center',
            padding: '32px 16px',
            textAlign: 'center' as const
        }
    };

    const renderStep1 = () => (
        <Form
            style={formResetStyle}
            onSubmit={(e) => {
                e.preventDefault();
                if (canProceed() && !isRegistering && !isUploading) {
                    handleNext();
                }
            }}
        >
            <div>
                <label htmlFor="ad-name" style={fieldLabelStyle}>Ad Name</label>
                <BorderedInput
                    id="ad-name"
                    value={name}
                    onChange={(value: string) => { setName(value); if (nameError) setNameError(null); }}
                    placeholder="Enter a name for your ad"
                    icon={<EditIcon />}
                />
            </div>
            {nameError ? (
                <p style={{color: colors.danger, fontSize: '13px', fontWeight: 600, marginTop: '12px', display: 'flex', gap: '6px', alignItems: 'flex-start'}}>
                    <TimesIcon style={{flexShrink: 0, marginTop: '2px'}} />
                    <span>{nameError}</span>
                </p>
            ) : (
                <p style={{color: colors.subtle, fontSize: '13px', marginTop: '12px'}}>
                    Choose a descriptive name that helps you identify this ad later.
                </p>
            )}
        </Form>
    );

    const renderStep2 = () => (
        <div>
            <p style={{color: colors.subtle, fontSize: '14px', marginBottom: '16px'}}>
                Select the organization this ad belongs to:
            </p>
            {organizations.length === 0 ? (
                <div style={styles.spinnerContainer}>
                    <Spinner size="lg" />
                    <p style={{color: colors.subtle, marginTop: '12px'}}>Loading organizations...</p>
                </div>
            ) : (
                organizations.map(org => (
                    <div
                        key={org.id}
                        style={org_id === org.id ? styles.orgCardSelected : styles.orgCard}
                        onClick={() => setOrgId(org.id)}
                    >
                        <div style={styles.orgIcon}>
                            <BuildingIcon />
                        </div>
                        <div style={{ flex: 1 }}>
                            <div style={styles.orgName}>{org.organizationName}</div>
                            {org.address && org.address.city && (
                                <div style={styles.orgCity}>{org.address.city}</div>
                            )}
                        </div>
                        {org_id === org.id && (
                            <CheckCircleIcon style={{color: colors.accent, fontSize: '20px'}} />
                        )}
                    </div>
                ))
            )}
        </div>
    );

    const renderStep3 = () => (
        <div style={styles.spinnerContainer}>
            {isRegistering ? (
                <>
                    <Spinner size="xl" />
                    <p style={{color: colors.subtle, marginTop: '16px', fontSize: '16px'}}>
                        Registering your ad...
                    </p>
                </>
            ) : isRegistered ? (
                <>
                    <LogoBadge variant="success" size={72}>
                        <CheckIcon />
                    </LogoBadge>
                    <p style={{color: colors.title, fontSize: '18px', fontWeight: 600, margin: 0}}>
                        Ad registered successfully!
                    </p>
                    <p style={{color: colors.subtle, fontSize: '14px', marginTop: '8px'}}>
                        Click Next to upload your media file.
                    </p>
                </>
            ) : (
                <>
                    <p style={{color: colors.body, fontSize: '15px', marginBottom: '16px'}}>
                        Ready to register your ad with the following details:
                    </p>
                    <div style={{width: '100%', maxWidth: '320px'}}>
                        <div style={styles.reviewItem}>
                            <span style={styles.reviewLabel}>Name</span>
                            <span style={styles.reviewValue}>{name}</span>
                        </div>
                        <div style={styles.reviewItem}>
                            <span style={styles.reviewLabel}>Organization</span>
                            <span style={styles.reviewValue}>
                                {(organizations.find(o => o.id === org_id) || {} as any).organizationName || org_id}
                            </span>
                        </div>
                    </div>
                    <p style={{color: colors.subtle, fontSize: '13px', marginTop: '16px'}}>
                        Click "Register Ad" to continue.
                    </p>
                    {createError && (
                        <p style={{color: colors.danger, fontSize: '13px', fontWeight: 600, marginTop: '14px', display: 'flex', gap: '6px', alignItems: 'center'}}>
                            <TimesIcon style={{flexShrink: 0}} />
                            <span>{createError}</span>
                        </p>
                    )}
                </>
            )}
        </div>
    );

    const MAX_REF_IMAGES = 3;

    const uploadReferenceImage = async (item: { id: string; file: File }) => {
        try {
            const data = await getOrgUploadUrl(org_id, item.file.name.trim());
            await uploadToS3(data['url'], item.file);
            setReferenceImages(prev => prev.map(r =>
                r.id === item.id ? { ...r, s3Uri: data['s3Uri'], uploading: false } : r));
        } catch (e) {
            console.error('Reference image upload failed:', e);
            setReferenceImages(prev => prev.map(r =>
                r.id === item.id ? { ...r, uploading: false, error: true } : r));
        }
    };

    const renderStepReferenceImages = () => {
        const handleRefFilesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            const files = Array.from(event.target.files || []);
            const room = MAX_REF_IMAGES - referenceImages.length;
            const toAdd = files.slice(0, Math.max(0, room));
            const newItems = toAdd.map((file) => ({
                id: `${Date.now()}-${Math.random().toString(36).slice(2)}-${file.name}`,
                file,
                previewUrl: URL.createObjectURL(file),
                uploading: true
            }));
            setReferenceImages(prev => [...prev, ...newItems]);
            newItems.forEach(uploadReferenceImage);
            if (refImageInputRef.current) refImageInputRef.current.value = '';
        };

        const handleRemoveRef = (id: string) => {
            setReferenceImages(prev => {
                const target = prev.find(r => r.id === id);
                if (target) URL.revokeObjectURL(target.previewUrl);
                return prev.filter(r => r.id !== id);
            });
        };

        const atMax = referenceImages.length >= MAX_REF_IMAGES;

        return (
            <div>
                <p style={{color: colors.subtle, fontSize: '14px', marginBottom: '14px'}}>
                    Optionally add up to {MAX_REF_IMAGES} reference images — your logo, a headshot, or
                    product photos. The AI will composite these into the ad. You can skip this step.
                </p>

                <input
                    type="file"
                    ref={refImageInputRef}
                    style={{display: 'none'}}
                    onChange={handleRefFilesChange}
                    accept="image/*"
                    multiple
                />

                <div style={{display: 'flex', gap: '12px', flexWrap: 'wrap'}}>
                    {referenceImages.map((item) => (
                        <div
                            key={item.id}
                            style={{
                                position: 'relative',
                                width: '104px',
                                height: '104px',
                                borderRadius: '12px',
                                overflow: 'hidden',
                                border: `1px solid ${item.error ? colors.danger : colors.border}`,
                                background: '#0f172a'
                            }}
                        >
                            <img
                                src={item.previewUrl}
                                alt={item.file.name}
                                style={{width: '100%', height: '100%', objectFit: 'cover', display: 'block', opacity: item.uploading ? 0.5 : 1}}
                            />
                            {item.uploading && (
                                <div style={{position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                                    <Spinner size="md" />
                                </div>
                            )}
                            {item.error && (
                                <div style={{position: 'absolute', bottom: 0, left: 0, right: 0, padding: '2px 4px', background: colors.danger, color: '#fff', fontSize: '10px', textAlign: 'center'}}>
                                    Upload failed
                                </div>
                            )}
                            <button
                                type="button"
                                onClick={() => handleRemoveRef(item.id)}
                                style={{
                                    position: 'absolute', top: '6px', right: '6px',
                                    background: 'rgba(15, 23, 42, 0.65)', border: 'none', borderRadius: '50%',
                                    width: '24px', height: '24px', display: 'flex', alignItems: 'center',
                                    justifyContent: 'center', cursor: 'pointer', color: '#ffffff'
                                }}
                                aria-label="Remove reference image"
                            >
                                <TimesIcon />
                            </button>
                        </div>
                    ))}

                    {!atMax && (
                        <div
                            style={{
                                width: '104px', height: '104px', borderRadius: '12px',
                                border: `2px dashed ${colors.border}`, background: '#fafafa',
                                display: 'flex', flexDirection: 'column', alignItems: 'center',
                                justifyContent: 'center', cursor: 'pointer', color: colors.subtle, gap: '6px'
                            }}
                            onClick={() => refImageInputRef.current && refImageInputRef.current.click()}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') refImageInputRef.current?.click(); }}
                        >
                            <UploadIcon />
                            <span style={{fontSize: '12px', fontWeight: 600}}>Add image</span>
                        </div>
                    )}
                </div>

                <p style={{color: colors.placeholder, fontSize: '12px', marginTop: '12px'}}>
                    {referenceImages.length}/{MAX_REF_IMAGES} added
                </p>
            </div>
        );
    };

    const renderStep4 = () => {
        const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
            const files = event.target.files;
            const selectedFile = files && files.length > 0 ? files[0] : null;
            // Reset the input so re-picking the same file still fires onChange.
            event.target.value = '';
            if (!selectedFile) {
                return;
            }
            setFile(selectedFile);
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
            setPreviewUrl(URL.createObjectURL(selectedFile));
            setFileValid(false);
            setFileDims(null);

            // Enforce the 100MB limit the UI advertises.
            if (selectedFile.size > 100 * 1024 * 1024) {
                setFileChecking(false);
                setValidationMsg('This file is larger than 100MB. Please choose a smaller file.');
                return;
            }

            setFileChecking(true);
            setValidationMsg(null);
            const dims = await readMediaDimensions(selectedFile);
            const error = validateLedMedia(dims);
            setFileDims(dims);
            setFileChecking(false);
            setFileValid(!error);
            setValidationMsg(error);
        };

        const handleRemoveFile = () => {
            setFile(null);
            if (previewUrl) {
                URL.revokeObjectURL(previewUrl);
            }
            setPreviewUrl(null);
            setFileValid(false);
            setFileChecking(false);
            setValidationMsg(null);
            setFileDims(null);
        };

        const isVideoFile = file && file.type.startsWith('video/');

        return (
            <div>
                {/* What makes a good LED image */}
                <div style={{
                    background: '#f5f3ff',
                    border: `1px solid ${colors.accentRing}`,
                    borderRadius: '14px',
                    padding: '16px 18px',
                    marginBottom: '18px'
                }}>
                    <p style={{margin: 0, fontSize: '14px', fontWeight: 700, color: colors.title}}>
                        Your ad shows on a 384 × 128 px LED screen (3:1 wide format)
                    </p>
                    <ul style={{
                        margin: '10px 0 0 0',
                        padding: 0,
                        listStyle: 'none',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '6px'
                    }}>
                        {[
                            'Use a 3:1 aspect ratio — ideal sizes are 384×128, 768×256, or 1152×384.',
                            'Use bold, high-contrast colors and large, legible text — small details vanish at this size.',
                            'Keep key content away from the edges and avoid large pure-white fills (very bright on LED).'
                        ].map((tip, i) => (
                            <li key={i} style={{
                                display: 'flex',
                                gap: '8px',
                                fontSize: '13px',
                                color: colors.body,
                                lineHeight: 1.4
                            }}>
                                <CheckCircleIcon style={{color: colors.accent, flexShrink: 0, marginTop: '2px'}} />
                                <span>{tip}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                {file && previewUrl && (
                    <div style={{
                        marginBottom: '16px',
                        // Break out of the 600px step column so the preview can be larger.
                        width: '672px',
                        maxWidth: 'calc(100vw - 48px)',
                        marginLeft: '50%',
                        transform: 'translateX(-50%)'
                    }}>
                        <p style={{
                            margin: '0 0 8px 0',
                            fontSize: '12px',
                            fontWeight: 600,
                            color: colors.subtle,
                            textAlign: 'center'
                        }}>
                            Preview on the car
                        </p>
                        <LedCarPreview
                            mediaUrl={previewUrl}
                            mediaType={isVideoFile ? 'VIDEO' : 'IMAGE'}
                            valid={!!fileValid}
                        />
                        <div style={{
                            marginTop: '10px',
                            display: 'flex',
                            justifyContent: 'center',
                            gap: '10px',
                            alignItems: 'center',
                            fontSize: '12px',
                            color: colors.subtle,
                            flexWrap: 'wrap'
                        }}>
                            <span style={{
                                fontWeight: 600,
                                color: colors.body,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                maxWidth: '220px'
                            }}>
                                {file.name}
                            </span>
                            {fileDims && <span>{fileDims.width}×{fileDims.height}</span>}
                            <span>{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                            <button
                                type="button"
                                onClick={handleRemoveFile}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: colors.danger,
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    padding: 0
                                }}
                            >
                                <TimesIcon /> Remove
                            </button>
                        </div>
                    </div>
                )}

                {/* Validation feedback */}
                {fileChecking && (
                    <p style={{fontSize: '13px', color: colors.subtle, textAlign: 'center', margin: '0 0 14px 0'}}>
                        Checking dimensions…
                    </p>
                )}
                {!fileChecking && validationMsg && (
                    <div style={{
                        background: colors.dangerRing,
                        border: `1px solid ${colors.danger}`,
                        borderRadius: '12px',
                        padding: '12px 14px',
                        marginBottom: '14px',
                        display: 'flex',
                        gap: '8px',
                        alignItems: 'flex-start'
                    }}>
                        <TimesIcon style={{color: colors.danger, flexShrink: 0, marginTop: '2px'}} />
                        <span style={{fontSize: '13px', color: colors.danger, fontWeight: 600, lineHeight: 1.4}}>
                            {validationMsg}
                        </span>
                    </div>
                )}
                {!fileChecking && file && fileValid && (
                    <p style={{
                        fontSize: '13px',
                        color: colors.success,
                        fontWeight: 600,
                        textAlign: 'center',
                        margin: '0 0 14px 0',
                        display: 'flex',
                        gap: '6px',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}>
                        <CheckCircleIcon /> Looks great — ready for the LED screen.
                    </p>
                )}

                <input
                    type="file"
                    ref={fileInputRef}
                    style={{display: 'none'}}
                    onChange={handleFileChange}
                    accept="image/*,video/*"
                />
                <div
                    style={file ? styles.uploadAreaActive : styles.uploadArea}
                    onClick={handleUploadClick}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleUploadClick(); }}
                >
                    <div style={styles.uploadIcon}>
                        <UploadIcon />
                    </div>
                    {file ? (
                        <p style={{color: colors.accent, fontSize: '14px', fontWeight: 600, margin: 0}}>
                            Click to choose a different file
                        </p>
                    ) : (
                        <>
                            <p style={{color: colors.title, fontSize: '15px', fontWeight: 600, margin: 0}}>
                                Drag and drop your file here
                            </p>
                            <p style={{color: colors.subtle, fontSize: '13px', marginTop: '4px'}}>
                                or click to browse
                            </p>
                            <p style={{color: colors.placeholder, fontSize: '12px', marginTop: '10px'}}>
                                Images or videos, 3:1 ratio (min 384×128), up to 100MB
                            </p>
                        </>
                    )}
                </div>
            </div>
        );
    };

    const renderStepAiGeneration = () => {
        const handlePromptChange = (value: string) => {
            const sanitized = value.replace(/<[^>]*>/g, '');
            const limited = sanitized.slice(0, 1000);
            setAiPrompt(limited);
        };

        const charCount = aiPrompt.length;
        const maxChars = 1000;

        return (
            <div>
                <p style={{color: colors.subtle, fontSize: '14px', marginBottom: '14px'}}>
                    Describe what you want your ad to look like. Be specific about colors, style, mood, and any text you want included.
                    In the next step, you can optionally add your logo, a headshot, or product photos for the AI to include in the ad.
                </p>
                <div style={{position: 'relative'}}>
                    <textarea
                        value={aiPrompt}
                        onChange={(e) => handlePromptChange(e.target.value)}
                        placeholder="Example: A vibrant, eye-catching image for a hair salon promotion. Use warm colors like gold and burgundy. Include the text 'Summer Special - 20% Off' in an elegant script font. Show a modern salon interior with stylish lighting."
                        style={{
                            width: '100%',
                            minHeight: '180px',
                            padding: '14px',
                            fontSize: '15px',
                            backgroundColor: '#ffffff',
                            border: `1px solid ${colors.border}`,
                            borderRadius: radii.input,
                            resize: 'vertical',
                            fontFamily: 'inherit',
                            color: colors.body,
                            lineHeight: 1.6,
                            outline: 'none',
                            transition: 'border-color 0.15s, box-shadow 0.15s',
                            boxSizing: 'border-box'
                        }}
                        onFocus={(e) => {
                            e.currentTarget.style.borderColor = colors.accent;
                            e.currentTarget.style.boxShadow = `0 0 0 3px ${colors.accentRing}`;
                        }}
                        onBlur={(e) => {
                            e.currentTarget.style.borderColor = colors.border;
                            e.currentTarget.style.boxShadow = 'none';
                        }}
                    />
                    <div style={{
                        position: 'absolute',
                        bottom: '12px',
                        right: '14px',
                        fontSize: '12px',
                        color: charCount > maxChars * 0.9 ? colors.danger : colors.placeholder
                    }}>
                        {charCount}/{maxChars}
                    </div>
                </div>
                <div style={{marginTop: '14px', padding: '12px 14px', background: '#f5f3ff', borderRadius: radii.input}}>
                    <p style={{fontSize: '13px', fontWeight: 600, color: colors.title, margin: '0 0 6px 0'}}>
                        Tips for better results:
                    </p>
                    <ul style={{margin: 0, paddingLeft: '20px', color: colors.muted, fontSize: '12px', lineHeight: 1.8}}>
                        <li>Describe the visual style (modern, vintage, minimalist, bold)</li>
                        <li>Mention specific colors or color schemes</li>
                        <li>Include any text or messaging you want displayed</li>
                        <li>Specify the mood (professional, fun, luxurious, friendly)</li>
                        <li>No need to describe your logo or products in detail &mdash; you can upload them as reference images in the next step</li>
                    </ul>
                </div>
            </div>
        );
    };

    const renderStepAiCreate = () => {
        return (
            <div style={styles.spinnerContainer}>
                {isRegistering ? (
                    <>
                        <Spinner size="xl" />
                        <p style={{color: colors.subtle, marginTop: '16px', fontSize: '16px'}}>
                            Creating your AI-generated ad...
                        </p>
                    </>
                ) : isComplete ? (
                    <>
                        <LogoBadge variant="success" size={72}>
                            <CheckIcon />
                        </LogoBadge>
                        <p style={{color: colors.title, fontSize: '18px', fontWeight: 600, margin: 0}}>
                            Ad creation started!
                        </p>
                        <p style={{color: colors.subtle, fontSize: '14px', marginTop: '8px'}}>
                            We're generating your ad. This may take a few moments.
                        </p>
                    </>
                ) : (
                    <div style={{textAlign: 'center'}}>
                        <LogoBadge size={72}>
                            <AutomationIcon style={{ fontSize: 32, color: colors.boltGold }} />
                        </LogoBadge>
                        <h3 style={{color: colors.title, fontSize: '18px', fontWeight: 700, margin: '0 0 10px 0'}}>
                            Ready to create your ad
                        </h3>
                        <p style={{color: colors.subtle, fontSize: '14px', lineHeight: 1.6, maxWidth: '380px', margin: '0 auto 18px'}}>
                            Click "Create Ad" and we'll start generating your custom ad based on your description. You'll be notified when it's ready.
                        </p>
                        <div style={{width: '100%', maxWidth: '320px', margin: '0 auto'}}>
                            <div style={styles.reviewItem}>
                                <span style={styles.reviewLabel}>Name</span>
                                <span style={styles.reviewValue}>{name}</span>
                            </div>
                            <div style={{...styles.reviewItem, borderBottom: 'none'}}>
                                <span style={styles.reviewLabel}>Organization</span>
                                <span style={styles.reviewValue}>
                                    {(organizations.find(o => o.id === org_id) || {} as any).organizationName || org_id}
                                </span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderStep5 = () => {
        return (
            <div>
                {isUploading ? (
                    <div style={styles.spinnerContainer}>
                        <Spinner size="xl" />
                        <p style={{color: colors.subtle, marginTop: '16px', fontSize: '16px'}}>
                            Uploading your media...
                        </p>
                    </div>
                ) : isComplete ? (
                    <div style={styles.spinnerContainer}>
                        <LogoBadge variant="success" size={72}>
                            <CheckIcon />
                        </LogoBadge>
                        <p style={{color: colors.title, fontSize: '18px', fontWeight: 600, margin: 0}}>
                            Ad created successfully!
                        </p>
                        <p style={{color: colors.subtle, fontSize: '14px', marginTop: '8px'}}>
                            Your ad is now ready to use in campaigns.
                        </p>
                    </div>
                ) : (
                    <>
                        <p style={{color: colors.subtle, fontSize: '14px', marginBottom: '16px'}}>
                            Review your ad details before finishing:
                        </p>
                        <div style={styles.reviewItem}>
                            <span style={styles.reviewLabel}>Ad Name</span>
                            <span style={styles.reviewValue}>{name}</span>
                        </div>
                        <div style={styles.reviewItem}>
                            <span style={styles.reviewLabel}>Organization</span>
                            <span style={styles.reviewValue}>
                                {(organizations.find(o => o.id === org_id) || {}).organizationName || org_id}
                            </span>
                        </div>
                        <div style={styles.reviewItem}>
                            <span style={styles.reviewLabel}>Ad ID</span>
                            <span style={styles.reviewValue}>{ad_id}</span>
                        </div>
                        <div style={{...styles.reviewItem, borderBottom: 'none'}}>
                            <span style={styles.reviewLabel}>Media File</span>
                            <span style={styles.reviewValue}>{file ? file.name : ''}</span>
                        </div>
                    </>
                )}
            </div>
        );
    };

    const renderStepAdType = () => {
        const typeCardStyle: React.CSSProperties = {
            background: '#ffffff',
            borderRadius: '14px',
            padding: '24px',
            border: `1px solid ${colors.border}`,
            cursor: 'pointer',
            transition: 'all 0.15s',
            textAlign: 'center',
            flex: 1
        };
        const typeCardSelected: React.CSSProperties = {
            ...typeCardStyle,
            borderColor: colors.accent,
            background: '#f5f3ff',
            boxShadow: `0 0 0 3px ${colors.accentRing}`
        };
        const iconContainerStyle: React.CSSProperties = {
            width: '52px',
            height: '52px',
            borderRadius: '14px',
            background: PURPLE_GRADIENT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 14px',
            fontSize: '22px',
            color: '#ffffff'
        };

        const aiTileBase = adType === 'ai' && !aiBlocked ? typeCardSelected : typeCardStyle;
        const aiTileStyle: React.CSSProperties = aiBlocked
            ? { ...aiTileBase, opacity: 0.55, cursor: 'not-allowed' }
            : aiTileBase;

        const handleAiClick = () => {
            if (aiBlocked) {
                setUpgradeModal({
                    message: aiBlockedReason || 'Upgrade your plan to use AI ad generation.',
                    ctaTier: aiNotInTier ? 'SILVER' : aiQuotaExhausted ? 'GOLD' : undefined,
                    offerTopUp: true
                });
                return;
            }
            setAdType('ai');
        };

        const aiBadge: React.ReactNode = (() => {
            if (aiUnlimited) {
                return (
                    <span style={{
                        display: 'inline-block',
                        marginTop: '6px',
                        fontSize: '11px',
                        fontWeight: 700,
                        padding: '2px 8px',
                        borderRadius: radii.pill,
                        background: '#d1fae5',
                        color: colors.success
                    }}>Unlimited</span>
                );
            }
            if (aiLimit !== null && aiLimit > 0) {
                return (
                    <span style={{
                        display: 'inline-block',
                        marginTop: '6px',
                        fontSize: '11px',
                        color: colors.subtle
                    }}>{aiUsed}/{aiLimit} used this period</span>
                );
            }
            return null;
        })();

        const aiTile = (
            <div style={aiTileStyle} onClick={handleAiClick}>
                <div style={iconContainerStyle}>
                    {aiBlocked ? <LockIcon /> : <AutomationIcon />}
                </div>
                <h4 style={{margin: '0 0 6px 0', fontSize: '16px', fontWeight: 600, color: colors.title}}>Generate with AI</h4>
                <p style={{margin: 0, fontSize: '13px', color: colors.subtle}}>
                    Let AI create visuals for you
                </p>
                {aiBadge}
            </div>
        );

        return (
            <div>
                <p style={{color: colors.subtle, fontSize: '14px', marginBottom: '20px', textAlign: 'center'}}>
                    How would you like to create your ad?
                </p>
                <div style={{display: 'flex', gap: '14px'}}>
                    <div
                        style={adType === 'upload' ? typeCardSelected : typeCardStyle}
                        onClick={() => setAdType('upload')}
                    >
                        <div style={iconContainerStyle}>
                            <UploadIcon />
                        </div>
                        <h4 style={{margin: '0 0 6px 0', fontSize: '16px', fontWeight: 600, color: colors.title}}>Upload Media</h4>
                        <p style={{margin: 0, fontSize: '13px', color: colors.subtle}}>
                            Use your own images or videos
                        </p>
                    </div>
                    {aiBlocked && aiBlockedReason ? (
                        <Tooltip content={aiBlockedReason}>
                            {aiTile}
                        </Tooltip>
                    ) : aiTile}
                </div>
            </div>
        );
    };

    const renderStepContent = () => {
        if (skipTypeStep) {
            if (effectiveAdType === 'ai') {
                switch (currentStep) {
                    case 1: return renderStep1();
                    case 2: return renderStep2();
                    case 3: return renderStepAiGeneration();
                    case 4: return renderStepReferenceImages();
                    case 5: return renderStepAiCreate();
                    default: return null;
                }
            } else {
                switch (currentStep) {
                    case 1: return renderStep1();
                    case 2: return renderStep2();
                    case 3: return renderStep3();
                    case 4: return renderStep4();
                    case 5: return renderStep5();
                    default: return null;
                }
            }
        } else {
            if (effectiveAdType === 'ai') {
                switch (currentStep) {
                    case 1: return renderStepAdType();
                    case 2: return renderStep1();
                    case 3: return renderStep2();
                    case 4: return renderStepAiGeneration();
                    case 5: return renderStepReferenceImages();
                    case 6: return renderStepAiCreate();
                    default: return null;
                }
            } else {
                switch (currentStep) {
                    case 1: return renderStepAdType();
                    case 2: return renderStep1();
                    case 3: return renderStep2();
                    case 4: return renderStep3();
                    case 5: return renderStep4();
                    case 6: return renderStep5();
                    default: return null;
                }
            }
        }
    };

    const getNextButtonText = () => {
        const registerStep = effectiveAdType === 'ai'
            ? totalSteps
            : (skipTypeStep ? 3 : 4);
        const finalStep = totalSteps;

        if (effectiveAdType === 'ai' && currentStep === registerStep) {
            if (isComplete) return 'Done';
            return 'Create Ad';
        }
        if (currentStep === registerStep && !isRegistered) return 'Register Ad';
        if (currentStep === finalStep) {
            if (isComplete) return 'Done';
            return 'Finish & Upload';
        }
        return 'Next';
    };

    const footerButtons = !isComplete && (
        <React.Fragment>
            <GradientButton
                appearance="secondary"
                onClick={currentStep === 1 ? dismiss : handleBack}
                isDisabled={isRegistering || isUploading}
            >
                <ArrowLeftIcon style={{ marginRight: 8 }} />
                {currentStep === 1 ? 'Cancel' : 'Back'}
            </GradientButton>
            <GradientButton
                onClick={handleNext}
                isDisabled={!canProceed() || isRegistering || isUploading}
                isLoading={isRegistering || isUploading}
            >
                {getNextButtonText()}
                {currentStep < totalSteps && !isRegistering && !isUploading && (
                    <ArrowRightIcon style={{ marginLeft: 8 }} />
                )}
            </GradientButton>
        </React.Fragment>
    );

    // Horizontal progress stepper across the top of the full-page layout. The
    // connecting line fills with the brand gradient up to the current step; the
    // active node carries a soft gold (AdVolt bolt) glow. Completed steps are
    // clickable to jump back. Labels are hidden on narrow widths.
    const renderStepper = () => {
        const steps = getStepList();
        return (
            <ol style={styles.stepperH}>
                {steps.map((label, i) => {
                    const n = i + 1;
                    const done = n < currentStep;
                    const active = n === currentStep;
                    const isLast = i === steps.length - 1;
                    const clickable = done && !isRegistering && !isUploading && !isComplete;
                    const nodeStyle: React.CSSProperties = {
                        width: '30px',
                        height: '30px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '13px',
                        fontWeight: 700,
                        flexShrink: 0,
                        transition: 'all 0.2s',
                        ...(done
                            ? { background: PURPLE_GRADIENT, color: '#ffffff' }
                            : active
                                ? { background: '#ffffff', color: colors.accent, border: `2px solid ${colors.accent}`, boxShadow: '0 0 0 4px rgba(251, 191, 36, 0.35)' }
                                : { background: '#ffffff', color: colors.placeholder, border: `2px solid ${colors.border}` })
                    };
                    return (
                        <li
                            key={label}
                            style={{
                                flex: isLast ? '0 0 auto' : 1,
                                minWidth: 0,
                                cursor: clickable ? 'pointer' : 'default'
                            }}
                            onClick={() => { if (clickable) { setCurrentStep(n); } }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <div style={nodeStyle}>{done ? <CheckIcon /> : n}</div>
                                {!isLast && (
                                    <div style={{
                                        flex: 1,
                                        height: '2px',
                                        margin: '0 10px',
                                        borderRadius: '2px',
                                        background: done ? PURPLE_GRADIENT : colors.border
                                    }} />
                                )}
                            </div>
                            {!isNarrow && (
                                <div style={{
                                    marginTop: '8px',
                                    paddingRight: '10px',
                                    fontSize: '13px',
                                    fontWeight: active ? 700 : 600,
                                    color: active ? colors.title : done ? colors.muted : colors.placeholder,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap' as const
                                }}>
                                    {label}
                                </div>
                            )}
                        </li>
                    );
                })}
            </ol>
        );
    };

    const wizardInner = (
        <React.Fragment>
            <div style={styles.body}>
                <div style={styles.header}>
                    <LogoBadge size={48} />
                    <h1 style={styles.title}>{getStepTitle()}</h1>
                    <p style={styles.subtitle}>Step {currentStep} of {totalSteps}</p>

                    <div style={styles.stepIndicator}>
                        {Array.from({length: totalSteps}, (_, i) => i + 1).map((step) => (
                            <div
                                key={step}
                                style={
                                    step === currentStep
                                        ? styles.stepDotActive
                                        : step < currentStep
                                            ? styles.stepDotCompleted
                                            : styles.stepDot
                                }
                            />
                        ))}
                    </div>
                </div>

                {renderStepContent()}
            </div>

            {footerButtons && <div style={styles.footer}>{footerButtons}</div>}
        </React.Fragment>
    );

    const wizardPage = (
        <div style={styles.pageWrap}>
            <div style={styles.pageHeader}>
                <div style={styles.centered}>
                    <div style={styles.headerTopRow}>
                        <div>
                            {!isComplete && <p style={styles.eyebrow}>Step {currentStep} of {totalSteps}</p>}
                            <h1 style={styles.pageTitle}>{getStepTitle()}</h1>
                        </div>
                        <span style={styles.ledPill}>
                            <span style={styles.ledSwatch} />
                            Runs on a 384 × 128 LED screen
                        </span>
                    </div>
                    {renderStepper()}
                </div>
            </div>
            <div style={styles.pageBody}>
                <div style={{ maxWidth: '600px', margin: '0 auto', width: '100%' }}>
                    {renderStepContent()}
                </div>
            </div>
            {footerButtons && (
                <div style={styles.pageFooter}>
                    <div style={{ ...styles.centered, display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
                        {footerButtons}
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <React.Fragment>
            {!props.hideButton && !isPageMode && (
                <GradientButton onClick={handleModalToggle}>
                    Create a New Ad
                </GradientButton>
            )}
            {isPageMode ? (
                <PageSection style={{ padding: 0, minHeight: '100%', background: '#ffffff' }}>
                    {wizardPage}
                </PageSection>
            ) : (
                <AppModal isOpen={isModalOpen} onClose={handleModalToggle} width="lg">
                    {wizardInner}
                </AppModal>
            )}

            <AppModal isOpen={!!upgradeModal} onClose={() => setUpgradeModal(null)} width="sm">
                <div style={{ padding: '40px 32px 28px' }}>
                    <LogoBadge size={56}>
                        <LockIcon style={{ fontSize: 28, color: colors.boltGold }} />
                    </LogoBadge>
                    <h3 style={{ margin: '0 0 10px 0', fontSize: '22px', fontWeight: 700, color: colors.title, textAlign: 'center', letterSpacing: '-0.5px' }}>
                        {upgradeModal?.ctaTier ? `Upgrade to ${upgradeModal.ctaTier}` : 'Choose a plan'}
                    </h3>
                    <p style={{ margin: '0 0 24px 0', color: colors.subtle, fontSize: '14px', lineHeight: 1.6, textAlign: 'center' }}>
                        {upgradeModal?.message}
                    </p>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', flexWrap: 'wrap' }}>
                        <Button variant="link" onClick={() => setUpgradeModal(null)} style={{ color: colors.subtle, fontWeight: 600 }}>
                            Not now
                        </Button>
                        {upgradeModal?.offerTopUp && (
                            <GradientButton
                                appearance="secondary"
                                onClick={() => {
                                    setUpgradeModal(null);
                                    setShowTopUp(true);
                                }}
                            >
                                Buy AI ad credits
                            </GradientButton>
                        )}
                        <GradientButton
                            onClick={() => {
                                setUpgradeModal(null);
                                handleModalToggle();
                                navigate('/plan');
                            }}
                        >
                            Go to plans
                        </GradientButton>
                    </div>
                </div>
            </AppModal>

            <TopUpModal
                isOpen={showTopUp}
                context="ai"
                onClose={() => setShowTopUp(false)}
                onPurchased={(sub) => {
                    if (sub) setSubscription(sub);
                }}
            />
        </React.Fragment>
    );
}

export { CreateAdWizardModal };
